import { useEffect, useRef, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { comprimirImagen, esImagen, ACCEPT_IMAGEN } from "@/lib/comprimirImagen";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Camera, Plus, RefreshCw, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

const LADO_MAYOR_FOTO = 1600, CALIDAD_FOTO = 0.8;
const LADO_MINIATURA = 400, CALIDAD_MINIATURA = 0.7;

// URLs firmadas de Spaces son absolutas (http...) y se usan directo; la ruta local de
// respaldo es relativa y necesita el token de autenticación, así que se trae como blob.
const BlobImg = ({ src, alt, className }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revocar = null;
    let activo = true;
    if (!src) { setUrl(null); return undefined; }
    if (src.startsWith("http")) { setUrl(src); return undefined; }
    api.get(src, { responseType: "blob" }).then((r) => {
      if (!activo) return;
      revocar = URL.createObjectURL(r.data);
      setUrl(revocar);
    }).catch(() => {});
    return () => { activo = false; if (revocar) URL.revokeObjectURL(revocar); };
  }, [src]);
  if (!url) return <div className={`${className} bg-slate-100 animate-pulse`} />;
  return <img src={url} alt={alt} className={className} />;
};

/** Fotos de puntos críticos de un trabajo (ingreso/salida). Las sube el asesor (permiso
 * trabajos.fotos). Cada punto admite una sola foto (se reemplaza al tomar otra) o varias
 * (según su casilla "Permite varias fotos"). Fotos libres (punto_id null) que ya existan de
 * antes se muestran de solo lectura en "Otras fotos" (ver/eliminar, sin opción de agregar). */
export function FotosTrabajo({ trabajoId, momento = "ingreso", soloLectura = false, soloMomento = null, onResumenCambio }) {
  const [tab, setTab] = useState(soloMomento || momento);
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState({});
  const [visor, setVisor] = useState(null);
  const inputsRef = useRef({});

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/trabajos/${trabajoId}/fotos`);
      setResumen(data);
      onResumenCambio?.(data);
    } catch (e) { toast.error(errorMsg(e)); }
  }, [trabajoId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!trabajoId) return;
    setCargando(true);
    cargar().finally(() => setCargando(false));
  }, [trabajoId, cargar]);

  if (!trabajoId) return null;
  if (cargando || !resumen) {
    return <p className="text-sm text-slate-500 py-4" data-testid="fotos-trabajo-cargando">Cargando fotos…</p>;
  }

  const fotosDe = (puntoId) => (resumen.fotos || []).filter((f) => f.punto_id === puntoId && f.momento === tab);
  const fotosLibres = fotosDe(null);
  const puntosTab = (resumen.puntos || []).filter((p) => p.momento === tab || p.momento === "ambos");
  const obligatoriosIds = new Set((resumen.obligatorios || []).filter((o) => o.momento === tab).map((o) => o.punto_id));
  const obligatoriosTab = (resumen.obligatorios || []).filter((o) => o.momento === tab);
  const totalObl = obligatoriosTab.length;
  const cumplidasObl = obligatoriosTab.filter((o) => o.cumplido).length;

  const subirUno = async (file, puntoId) => {
    if (!file) return false;
    if (!esImagen(file)) { toast.error("Elija una imagen"); return false; }
    const slotKey = puntoId || "__libre__";
    setSubiendo((s) => ({ ...s, [slotKey]: 5 }));
    try {
      const [fotoBlob, miniBlob] = await Promise.all([
        comprimirImagen(file, LADO_MAYOR_FOTO, CALIDAD_FOTO),
        comprimirImagen(file, LADO_MINIATURA, CALIDAD_MINIATURA),
      ]);
      const fd = new FormData();
      fd.append("archivo", fotoBlob, "foto.jpg");
      fd.append("miniatura", miniBlob, "miniatura.jpg");
      if (puntoId) fd.append("punto_id", puntoId);
      fd.append("momento", tab);
      fd.append("tomada_en", String(file.lastModified || Date.now()));
      await api.post(`/trabajos/${trabajoId}/fotos`, fd, {
        onUploadProgress: (ev) => {
          if (ev.total) setSubiendo((s) => ({ ...s, [slotKey]: Math.round((ev.loaded / ev.total) * 100) }));
        },
      });
      await cargar();
      return true;
    } catch (e) { toast.error(errorMsg(e)); return false; }
    finally { setSubiendo((s) => { const n = { ...s }; delete n[slotKey]; return n; }); }
  };

  // Punto sin "varias fotos": la nueva reemplaza a la que ya hubiera en ese momento (el
  // backend también lo valida), pidiendo confirmación cuando ya había una.
  const subirConReemplazo = async (file, puntoId, habiaFoto) => {
    if (!file) return;
    if (habiaFoto && !window.confirm("¿Reemplazar la foto actual de este punto por la nueva?")) return;
    const ok = await subirUno(file, puntoId);
    if (ok) toast.success(habiaFoto ? "Foto reemplazada" : "Foto subida");
  };

  // Punto con "varias fotos" o "Foto adicional": se pueden elegir/tomar varias a la vez.
  const subirVarios = async (files, puntoId) => {
    if (!files.length) return;
    let subidas = 0;
    for (const file of files) { if (await subirUno(file, puntoId)) subidas += 1; } // eslint-disable-line no-await-in-loop
    if (subidas > 0) toast.success(subidas > 1 ? `${subidas} fotos subidas` : "Foto subida");
  };

  const eliminarFoto = async (foto) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/trabajo-fotos/${foto.id}`);
      toast.success("Foto eliminada");
      setVisor(null);
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const abrirSelector = (slotKey) => !soloLectura && inputsRef.current[slotKey]?.click();

  // Devuelve las celdas (una por foto + una "+" si admite varias, o una sola celda si no)
  // que representan a un punto dentro de la grilla de fotos de la pestaña activa.
  const celdasDePunto = (p) => {
    const puntoId = p.id; // null para "Foto adicional"
    const nombre = p.nombre;
    const multiples = puntoId ? !!p.multiples : true;
    const fotos = fotosDe(puntoId);
    const obligatoria = puntoId && obligatoriosIds.has(puntoId);
    const slotKey = puntoId || "__libre__";
    const progreso = subiendo[slotKey];

    if (!multiples) {
      const foto = fotos[0];
      return [(
        <div key={slotKey} data-testid={`foto-punto-${slotKey}-${tab}`} className="space-y-1">
          <div className="relative w-full aspect-square">
            <button type="button" data-testid={`foto-punto-recuadro-${slotKey}-${tab}`}
              onClick={() => (foto ? setVisor({ fotos: [foto], idx: 0, puntoId, nombre }) : abrirSelector(slotKey))}
              className={`absolute inset-0 w-full h-full rounded-lg border overflow-hidden flex items-center justify-center
                ${foto ? "border-slate-200" : "border-dashed border-2 border-slate-300 bg-slate-50 hover:bg-slate-100"}`}>
              {foto ? (
                <BlobImg src={foto.url_miniatura} alt={nombre} className="w-full h-full object-cover" />
              ) : (
                <Camera size={22} className="text-slate-300" />
              )}
            </button>
            {progreso !== undefined && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                <div className="w-3/4 h-1.5 bg-white/30 rounded-full overflow-hidden">
                  <div className="h-full bg-white transition-all" style={{ width: `${progreso}%` }} />
                </div>
              </div>
            )}
            {!soloLectura && foto && (
              <button type="button" title="Reemplazar foto" data-testid={`foto-punto-reemplazar-${slotKey}-${tab}`}
                onClick={() => abrirSelector(slotKey)}
                className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow hover:bg-indigo-700">
                <RefreshCw size={12} />
              </button>
            )}
          </div>
          {!soloLectura && (
            <input ref={(el) => (inputsRef.current[slotKey] = el)} type="file" accept={ACCEPT_IMAGEN} capture="environment"
              data-testid={`foto-punto-input-${slotKey}-${tab}`} className="hidden"
              onChange={(e) => { subirConReemplazo(e.target.files?.[0], puntoId, !!foto); e.target.value = ""; }} />
          )}
          <p className="text-[11px] text-slate-600 leading-tight text-center" title={nombre}>
            {nombre}
            {obligatoria && <span className="block text-amber-600 font-semibold">Obligatoria</span>}
          </p>
        </div>
      )];
    }

    const celdas = fotos.map((foto, idx) => (
      <div key={foto.id} data-testid={`foto-punto-${slotKey}-${tab}-${idx}`} className="space-y-1">
        <button type="button" data-testid={`foto-punto-recuadro-${slotKey}-${tab}-${idx}`}
          onClick={() => setVisor({ fotos, idx, puntoId, nombre })}
          className="relative block w-full aspect-square rounded-lg border border-slate-200 overflow-hidden">
          <BlobImg src={foto.url_miniatura} alt={nombre} className="w-full h-full object-cover" />
        </button>
        <p className="text-[11px] text-slate-600 leading-tight text-center" title={nombre}>
          {nombre}
          {obligatoria && idx === 0 && <span className="block text-amber-600 font-semibold">Obligatoria</span>}
        </p>
      </div>
    ));
    celdas.push(
      <div key={`${slotKey}-add`} className="space-y-1">
        <div className="relative w-full aspect-square">
          <button type="button" title={fotos.length ? "Agregar otra foto" : nombre}
            data-testid={`foto-punto-agregar-${slotKey}-${tab}`} onClick={() => abrirSelector(slotKey)}
            className="absolute inset-0 w-full h-full rounded-lg border-dashed border-2 border-slate-300 bg-slate-50 hover:bg-slate-100 flex items-center justify-center">
            {fotos.length ? <Plus size={22} className="text-indigo-400" /> : <Camera size={22} className="text-slate-300" />}
          </button>
          {progreso !== undefined && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
              <div className="w-3/4 h-1.5 bg-white/30 rounded-full overflow-hidden">
                <div className="h-full bg-white transition-all" style={{ width: `${progreso}%` }} />
              </div>
            </div>
          )}
          {!soloLectura && (
            <input ref={(el) => (inputsRef.current[slotKey] = el)} type="file" accept={ACCEPT_IMAGEN} capture="environment"
              multiple data-testid={`foto-punto-input-${slotKey}-${tab}`} className="hidden"
              onChange={(e) => { subirVarios(Array.from(e.target.files || []), puntoId); e.target.value = ""; }} />
          )}
        </div>
        <p className="text-[11px] text-slate-600 leading-tight text-center" title={nombre}>
          {fotos.length ? "Agregar otra" : nombre}
          {obligatoria && !fotos.length && <span className="block text-amber-600 font-semibold">Obligatoria</span>}
        </p>
      </div>
    );
    return celdas;
  };

  return (
    <div data-testid="fotos-trabajo" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        {!soloMomento && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="ingreso" data-testid="fotos-tab-ingreso">Ingreso</TabsTrigger>
              <TabsTrigger value="salida" data-testid="fotos-tab-salida">Salida</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        {totalObl > 0 && (
          <span data-testid="fotos-contador-obligatorias"
            className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              cumplidasObl < totalObl ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>
            Obligatorias: {cumplidasObl} de {totalObl}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {puntosTab.flatMap((p) => celdasDePunto(p))}
      </div>

      {fotosLibres.length > 0 && (
        <div className="space-y-1.5 pt-2 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase">Otras fotos</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {fotosLibres.map((foto, idx) => (
              <button type="button" key={foto.id} data-testid={`foto-libre-recuadro-${tab}-${idx}`}
                onClick={() => setVisor({ fotos: fotosLibres, idx, puntoId: null, nombre: "Otras fotos" })}
                className="relative block w-full aspect-square rounded-lg border border-slate-200 overflow-hidden">
                <BlobImg src={foto.url_miniatura} alt="Otras fotos" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!visor} onOpenChange={(o) => { if (!o) setVisor(null); }}>
        <DialogContent data-testid="foto-visor-dialog" className="max-w-lg">
          {visor && (() => {
            const foto = visor.fotos[visor.idx];
            if (!foto) return null;
            return (
              <div className="space-y-3">
                <DialogHeader><DialogTitle>{visor.nombre}</DialogTitle></DialogHeader>
                <div className="relative">
                  <BlobImg src={foto.url} alt={visor.nombre}
                    className="w-full max-h-[60vh] object-contain rounded-md bg-slate-900" />
                  {visor.fotos.length > 1 && (
                    <>
                      <button type="button" data-testid="foto-visor-anterior"
                        onClick={() => setVisor((v) => ({ ...v, idx: (v.idx - 1 + v.fotos.length) % v.fotos.length }))}
                        className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center">
                        <ChevronLeft size={16} />
                      </button>
                      <button type="button" data-testid="foto-visor-siguiente"
                        onClick={() => setVisor((v) => ({ ...v, idx: (v.idx + 1) % v.fotos.length }))}
                        className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center">
                        <ChevronRight size={16} />
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">{visor.idx + 1} / {visor.fotos.length}</span>
                  {!soloLectura && (
                    <Button variant="outline" data-testid="foto-visor-eliminar-btn"
                      className="border-pink-300 text-pink-600 hover:bg-pink-50" onClick={() => eliminarFoto(foto)}>
                      <Trash2 size={14} className="mr-1.5" /> Eliminar
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
